import { entityStore, Measurements } from '~/utility/BinSentry-ui-utility';
import '../../common/bn-form-field';
import '../../bins/parts/bin-profile-svg';
import { LitEntity, SirenActionMixin, html, css } from '../../utility/lit-entity';
import { bannerStyles } from '../../styles/banner-styles';
import { sharedStyles } from '../../styles/shared-styles';
import './bin-dimension-confirm-modal';

const deg2rad = (n) => n * Math.PI / 180;
const rad2deg = (n) => n * 180 / Math.PI;

const PROFILE = {
  CENTER: 1,
  SIDE: 2.,
};

// Center Discharge
// |________| _
//  \     a/   |
//   \    /    | height
//    \__/    _|
//
//       '--'
//       base
//
// Side Discharge
// |____| _
// |  a/   |
// |  /    | height
// |_/    _|
//
//   '--'
//   base

function hopperBase({ profile, bodyDiameter, hopperDiameter }) {
  const base = bodyDiameter - hopperDiameter;
  switch (profile) {
  case PROFILE.CENTER: return base / 2;
  case PROFILE.SIDE: return base;
  default: throw new Error('unknown profile');
  }
}

const heightFromAngle = ({ angle, base }) => Math.round(base * Math.tan(deg2rad(angle)) * 100) / 100;
const angleFromHeight = ({ height, base }) => Math.round(rad2deg(Math.atan(height / base)) * 100) / 100;

// acceptable values
const DIAMETER_MIN_VALUE = 72;
const DIAMETER_MAX_VALUE = 192;
const HOPPER_DIAMETER_MIN_VALUE = 8;
const HOPPER_DIAMETER_MAX_VALUE = 20;
const HOPPER_DIAMETER_MAX_RATIO = 0.25;
const HOPPER_HEIGHT_MIN_VALUE = 36;
const HOPPER_HEIGHT_MAX_VALUE = 108;
const BODY_HEIGHT_MIN_VALUE = 36;
const BODY_HEIGHT_MAX_VALUE = 360;
const BODY_HEIGHT_MAX_RATIO = 9999999; // TBD
const RING_COUNT_MIN_VALUE = 0;
const RING_COUNT_MAX_VALUE = 6;
const TOP_ANGLE_MIN_VALUE = 25;
const TOP_ANGLE_MAX_VALUE = 40;
const TOP_DIAMETER_MIN_VALUE = 8;
const TOP_DIAMETER_MAX_VALUE = 30;
const TOP_DIAMETER_MAX_RATIO = 0.35;
const LIP_HEIGHT_MIN_VALUE = 1;
const LIP_HEIGHT_MAX_VALUE = 8;
const DEVICE_DEPTH_MIN_VALUE = 6;
const DEVICE_DEPTH_MAX_VALUE = 13;

class WorkOrderBinDimensions extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.bodyHeight = 0.0;
    this.lipHeight = 0.0;
    this.deviceDepth = 0.0;
    this.diameter = 0.0;
    this.hopperAngle = 0.0;
    this.hopperHeight = 0.0;
    this.hopperDiameter = 0.0;
    this.profile = 1;
    this.ringCount = 0.0;
    this.topHeight = 0.0;
    this.topDiameter = 0.0;
    this.isConfirmModalHidden = true;
    this.confirmModalMessage = '';
  }

  static get properties() {
    return {
      bodyHeight: { type: Number },
      lipHeight: { type: Number },
      deviceDepth: { type: Number },
      diameter: { type: Number },
      hopperAngle: { type: Number },
      hopperDiameter: { type: Number },
      hopperHeight: { type: Number },
      profile: { type: Number },
      ringCount: { type: Number },
      topHeight: { type: Number },
      topAngle: { type: Number },
      topDiameter: { type: Number },
      isConfirmModalHidden: { type: Boolean },
      confirmModalMessage: { type: String },
    };
  }

  static get styles() {
    return [sharedStyles, bannerStyles, css`
      :host {
        display: flex;
      }
      .form {
        flex-grow: 1;
      }
      .profile {
        width: 120px;
        height: 200px;
        margin-left: 1em;
        position: sticky;
        top: 0;
      }
      @media(max-width: 1000px) {
        :host {
          flex-direction: column;
        }
        .profile { width: 100%; }
      }
    `];
  }

  render() {
    const measurement = Measurements.get('dimension');
    return html`
      <div class="form">
        <div class="form-field">
          <label>
            Bin Profile
            <select name="dimensions[profile]" @change="${this._onInputProfile}">
              <option value="${PROFILE.CENTER}" ?selected="${this.profile === PROFILE.CENTER}">Center Discharge</option>
              <option value="${PROFILE.SIDE}" ?selected="${this.profile === PROFILE.SIDE}">Side Discharge</option>
            </select>
          </label>
        </div>
        <bn-form-field
          select
          type="number"
          name="diameter"
          title="Body Diameter"
          .value="${this.diameter}"
          @input="${this._onInputBodyDiameter}"
        >
          <span slot="suffix" class="suffix">${measurement.unit}</span>
        </bn-form-field>
        <bn-form-field
          select
          type="number"
          name="hopper-diameter"
          title="Hopper Opening Diameter"
          .value="${this.hopperDiameter}"
          @input="${this._onInputHopperDiameter}"
        >
          <span slot="suffix" class="suffix">${measurement.unit}</span>
        </bn-form-field>
        <bn-form-field
          select
          type="number"
          name="hopper-angle"
          title="Hopper Angle"
          min="0"
          max="90"
          .value="${this.hopperAngle}"
          @input="${this._onInputHopperAngle}"
          ?disabled="${this.profile === PROFILE.SIDE}"
        >
          <span slot="suffix" class="suffix">degrees</span>
        </bn-form-field>
        <bn-form-field
          select
          type="number"
          name="dimensions[hopper][height]"
          title="Hopper Height"
          .value="${this.hopperHeight}"
          @input="${this._onInputHopperHeight}"
          ?disabled="${this.profile === PROFILE.CENTER}"
        >
          <span slot="suffix" class="suffix">${measurement.unit}</span>
        </bn-form-field>
        <div class="banner banner--info">
          You may need to climb the bin to complete the following steps.
        </div>
        <bn-form-field
          select
          type="number"
          name="dimensions[body][height]"
          title="Body Height"
          .value="${this.bodyHeight}"
          @input="${(e) => this.bodyHeight = this._validDistance(e.target.input.value)}"
        >
          <span slot="suffix" class="suffix">${measurement.unit}</span>
        </bn-form-field>
        <bn-form-field
          select
          type="number"
          name="dimensions[ringCount]"
          title="Ring Count"
          .value="${this.ringCount}"
          @input="${(e) => this.ringCount = this._validDistance(e.target.input.value)}"
        ></bn-form-field>
        <bn-form-field
          disabled
          select
          type="number"
          name="dimensions[top][height]"
          title="Top Height"
          .value="${this.topHeight}"
          @input="${(e) => this.topHeight = this._validDistance(e.target.input.value)}"
        >
          <span slot="suffix" class="suffix">${measurement.unit}</span>
        </bn-form-field>
        <bn-form-field
          select
          type="number"
          name="top-angle"
          title="Top Angle"
          min="0"
          max="90"
          .value="${this.topAngle}"
          @input="${this._onInputTopAngle}"
        >
          <span slot="suffix" class="suffix">degrees</span>
        </bn-form-field>
        <bn-form-field
          select
          type="number"
          name="top-diameter"
          title="Top Opening Diameter"
          .value="${this.topDiameter}"
          @input="${this._onInputTopDiameter}"
        >
          <span slot="suffix" class="suffix">${measurement.unit}</span>
        </bn-form-field>
        <bn-form-field
          select
          type="number"
          name="dimensions[lipHeight]"
          title="Lip Height"
          .value="${this.lipHeight}"
          @input="${(e) => this.lipHeight = this._validDistance(e.target.input.value)}"
        >
          <span slot="suffix" class="suffix">${measurement.unit}</span>
        </bn-form-field>
        <div class="banner banner--info">
          Install the device.
        </div>
        <bn-form-field
          select
          type="number"
          name="dimensions[deviceDepth]"
          title="Sensor Length"
          .value="${this.deviceDepth}"
          @input="${(e) => this.deviceDepth = this._validDistance(e.target.input.value)}"
        >
          <span slot="suffix" class="suffix">${measurement.unit}</span>
        </bn-form-field>
        <bn-flash></bn-flash>
        <input type="hidden" id="binDimensionsCopiedBinId" name="binDimensionsCopiedBinId" value="">
        <div class="form-actions">
          <button class="button button--contained" @click="${this._preSubmit}">Save</button>
          <button class="button button--outlined" @click="${this._autofill}">Autofill</button>
        </div>
        <bin-dimension-confirm-modal
          ?hide="${this.isConfirmModalHidden}"
          .message="${this.confirmModalMessage}"
          @confirm="${this.onModalConfirm}"
          @cancel="${this.onModalCancel}"
          @close="${this.onModalCancel}"
        ></bin-dimension-confirm-modal>
      </div>
      <div>
        <div class="profile">
          <bin-profile-svg nodevice .bindimensions="${this._getBinDimensions()}"></bin-profile-svg>
        </div>
      </div>
    `;
  }

  onModalConfirm() {
    this.isConfirmModalHidden = true;
    this.confirmModalMessage = '';
    this._submit();
  }

  onModalCancel() {
    this.isConfirmModalHidden = true;
    this.confirmModalMessage = '';
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this._set();
  }

  _validAngle(value) {
    let angle = Number(value);
    if (angle > 89) {
      angle = 89;
    } else if (angle < 1) {
      angle = 1;
    }
    return angle;
  }

  _validDistance(value) {
    return Math.max(0, Number(value));
  }

  _onInputProfile(e) {
    this.profile = Number(e.target.value);
    this._updateHopperAngle();
  }

  _onInputBodyDiameter(e) {
    this.diameter = this._validDistance(e.target.input.value);
    this._updateHopperHeight();
    this._updateTopHeight();
  }

  _onInputHopperAngle(e) {
    this.hopperAngle = this._validAngle(e.target.input.value);
    this._updateHopperHeight();
  }

  _onInputHopperDiameter(e) {
    this.hopperDiameter = this._validDistance(e.target.input.value);
    this._updateHopperHeight();
  }

  _onInputHopperHeight(e) {
    this.hopperHeight = this._validDistance(e.target.input.value);
    this._updateHopperAngle();
  }

  _onInputTopAngle(e) {
    this.topAngle = this._validAngle(e.target.input.value);
    this._updateTopHeight();
  }

  _onInputTopDiameter(e) {
    this.topDiameter = this._validDistance(e.target.input.value);
    this._updateTopHeight();
  }

  _updateTopHeight() {
    try {
      const base = (this.diameter - this.topDiameter) / 2;
      this.topHeight = heightFromAngle({ base, angle: this.topAngle });
    } catch (ex) {
      console.error('error calculating height from angle', ex.message); // eslint-disable-line no-console
    }
  }

  _updateHopperAngle() {
    try {
      const { profile, diameter: bodyDiameter, hopperDiameter } = this;
      const base = hopperBase({ profile, bodyDiameter, hopperDiameter });
      this.hopperAngle = angleFromHeight({ height: this.hopperHeight, base });
    } catch (ex) {
      console.error('error calculating angle from height', ex.message); // eslint-disable-line no-console
    }
  }

  _updateHopperHeight() {
    try {
      const { profile, diameter: bodyDiameter, hopperDiameter } = this;
      const base = hopperBase({ profile, bodyDiameter, hopperDiameter });
      this.hopperHeight = heightFromAngle({ base, angle: this.hopperAngle });
    } catch (ex) {
      console.error('error calculating height from angle', ex.message); // eslint-disable-line no-console
    }
  }

  _getBinDimensions() {
    const measurement = Measurements.get('dimension');
    return {
      profile: this.profile,
      ringCount: this.ringCount,
      deviceDepth: measurement.reverse({ dimension: this.deviceDepth }),
      lipHeight: measurement.reverse({ dimension: this.lipHeight }),
      top: {
        height: measurement.reverse({ dimension: this.topHeight }),
        angle: this.topAngle,
        truncatedRadius: measurement.reverse({ dimension: this.topDiameter / 2 }),
      },
      body: {
        height: measurement.reverse({ dimension: this.bodyHeight }),
        radius: measurement.reverse({ dimension: this.diameter / 2 }),
      },
      hopper: {
        height: measurement.reverse({ dimension: this.hopperHeight }),
        angle: this.hopperAngle,
        truncatedRadius: measurement.reverse({ dimension: this.hopperDiameter / 2 }),
      },
      totalHeight: measurement.reverse({ dimension: this.topHeight + this.bodyHeight + this.hopperHeight }),
    };
  }

  _validateFields() {
    // Calculate min and max angles
    const base = hopperBase({ profile:this.profile, bodyDiameter: this.diameter, hopperDiameter: this.hopperDiameter });
    const hopperAngleMaxValue = angleFromHeight({ height: HOPPER_HEIGHT_MAX_VALUE, base });
    const hopperAngleMinValue = angleFromHeight({ height: HOPPER_HEIGHT_MIN_VALUE, base });

    // Validation of field values
    const validationRules = [
      { value: this.diameter, min: DIAMETER_MIN_VALUE, max: DIAMETER_MAX_VALUE, name: 'Body Diameter' },
      { value: this.hopperDiameter, min: HOPPER_DIAMETER_MIN_VALUE, max: HOPPER_DIAMETER_MAX_VALUE, name: 'Hopper Opening Diameter' },
      { value: this.hopperHeight, min: HOPPER_HEIGHT_MIN_VALUE, max: HOPPER_HEIGHT_MAX_VALUE, name: 'Hopper Height', condition: this.profile === PROFILE.SIDE },
      { value: this.hopperAngle, min: hopperAngleMinValue, max: hopperAngleMaxValue, name: 'Hopper Angle', condition: this.profile === PROFILE.CENTER },
      { value: this.bodyHeight, min: BODY_HEIGHT_MIN_VALUE, max: BODY_HEIGHT_MAX_VALUE, name: 'Body Height' },
      { value: this.ringCount, min: RING_COUNT_MIN_VALUE, max: RING_COUNT_MAX_VALUE, name: 'Ring Count' },
      { value: this.topAngle, min: TOP_ANGLE_MIN_VALUE, max: TOP_ANGLE_MAX_VALUE, name: 'Top Angle' },
      { value: this.topDiameter, min: TOP_DIAMETER_MIN_VALUE, max: TOP_DIAMETER_MAX_VALUE, name: 'Top Opening Diameter' },
      { value: this.lipHeight, min: LIP_HEIGHT_MIN_VALUE, max: LIP_HEIGHT_MAX_VALUE, name: 'Lip Height' },
      { value: this.deviceDepth, min: DEVICE_DEPTH_MIN_VALUE, max: DEVICE_DEPTH_MAX_VALUE, name: 'Sensor Length' },
    ];
    this.confirmModalMessage = '';
    validationRules.forEach(rule => {
      if ((rule.condition === undefined || rule.condition) &&
          (rule.value < rule.min || rule.value > rule.max)) {
        this.confirmModalMessage += `Input value of ${rule.value} for ${rule.name} is out of the standard range from ${rule.min} to ${rule.max}.\n`;
      }
    });

    // Validate ratios
    const ratios = [
      { value: this.hopperDiameter / this.diameter, max: HOPPER_DIAMETER_MAX_RATIO, label: 'Hopper Opening Diameter' },
      { value: this.hopperHeight / this.diameter, max: BODY_HEIGHT_MAX_RATIO, label: 'Hopper Height' },
      { value: this.topDiameter / this.diameter, max: TOP_DIAMETER_MAX_RATIO, label: 'Top Opening Diameter' },
    ];
    ratios.forEach(({ value, max, label }) => {
      if (value > max) {
        this.confirmModalMessage += `${label} is larger than ${max * 100}% of the Body Diameter.\n`;
      }
    });

    return this.confirmModalMessage === '';
  }

  _set() {
    const measurement = Measurements.get('dimension');
    const action = this.entity.getActionByName('update-dimensions');
    this.diameter = measurement.convert({ dimension: action.getFieldByName('dimensions[body][radius]').value * 2 });
    this.bodyHeight = measurement.convert({ dimension: action.getFieldByName('dimensions[body][height]').value });
    this.lipHeight = measurement.convert({ dimension: action.getFieldByName('dimensions[lipHeight]').value });
    this.deviceDepth = measurement.convert({ dimension: action.getFieldByName('dimensions[deviceDepth]').value });
    this.hopperHeight = measurement.convert({ dimension: action.getFieldByName('dimensions[hopper][height]').value });
    this.hopperDiameter = measurement.convert({ dimension: action.getFieldByName('dimensions[hopper][truncatedRadius]').value * 2 });
    this.profile = action.getFieldByName('dimensions[profile]').value;
    this.ringCount = action.getFieldByName('dimensions[ringCount]').value;
    this.topHeight = measurement.convert({ dimension: action.getFieldByName('dimensions[top][height]').value });
    this.topDiameter = measurement.convert({ dimension: action.getFieldByName('dimensions[top][truncatedRadius]').value * 2 });

    const { profile, diameter: bodyDiameter, hopperDiameter, topDiameter, topHeight } = this;
    this.hopperAngle = angleFromHeight({ height: this.hopperHeight, base: hopperBase({ profile, bodyDiameter, hopperDiameter }) });
    this.topAngle = angleFromHeight({ height: topHeight, base: (bodyDiameter - topDiameter) / 2 });
  }

  _preSubmit() {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    if (this._validateFields()) {
      this._submit();
    } else {
      this.isConfirmModalHidden = false;
    }
  }

  async _submit() {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    const action = this.entity.getActionByName('update-dimensions');
    const d = this._getBinDimensions();
    action.getFieldByName('dimensions[profile]').value = d.profile;
    action.getFieldByName('dimensions[ringCount]').value = d.ringCount;
    action.getFieldByName('dimensions[lipHeight]').value = d.lipHeight;
    action.getFieldByName('dimensions[deviceDepth]').value = d.deviceDepth;
    action.getFieldByName('dimensions[top][truncatedRadius]').value = d.top.truncatedRadius;
    action.getFieldByName('dimensions[top][height]').value = d.top.height;
    action.getFieldByName('dimensions[top][angle]').value = d.top.angle;
    action.getFieldByName('dimensions[body][height]').value = d.body.height;
    action.getFieldByName('dimensions[body][radius]').value = d.body.radius;
    action.getFieldByName('dimensions[hopper][height]').value = d.hopper.height;
    action.getFieldByName('dimensions[hopper][angle]').value = d.hopper.angle;
    action.getFieldByName('dimensions[hopper][truncatedRadius]').value = d.hopper.truncatedRadius;
    const fields = this.getActionFields(action);
    try {
      const { entity, error } = await this.submitAction(action, fields);
      if (error) {
        throw new Error(error);
      }
      entityStore.invalidateHref(this.href);
      flash.success('Bin dimension have been saved.');
      this.dispatchEvent(new CustomEvent('response', { detail: { entity }, bubbles: true, composed: true }));
    } catch (error) {
      flash.critical(html`
        There was a problem saving the bin dimensions.
        <div><code>${error.message}</code></div>
      `);
    }
  }

  async _autofill () {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    let autofillEntity, sourceBin, sourceBarn;
    try {
      const autofillHref = this.entity.getLinkByRel('autofill-dimensions').href;
      autofillEntity = await this.fetchSiren(autofillHref);
      ({ sourceBin, sourceBarn } = autofillEntity.properties);
    } catch (error) {
      const errorMessage = error?.error?.message || error.message || 'Unknown error occurred';
      flash.critical(html`
        There was a problem fetching the installed bins on this farm.
        <div><code>${errorMessage}</code></div>
        `);
      return;
    }
    // copy the dimensions from the source bin
    try {
      const action = this.entity.getActionByName('update-dimensions');
      const d = sourceBin.dimensions;
      action.getFieldByName('dimensions[profile]').value = d.profile;
      action.getFieldByName('dimensions[ringCount]').value = d.ringCount;
      action.getFieldByName('dimensions[lipHeight]').value = d.lipHeight;
      action.getFieldByName('dimensions[deviceDepth]').value = d.deviceDepth;
      action.getFieldByName('dimensions[top][truncatedRadius]').value = d.top.truncatedRadius;
      action.getFieldByName('dimensions[top][height]').value = d.top.height;
      action.getFieldByName('dimensions[top][angle]').value = d.top.angle;
      action.getFieldByName('dimensions[body][height]').value = d.body.height;
      action.getFieldByName('dimensions[body][radius]').value = d.body.radius;
      action.getFieldByName('dimensions[hopper][height]').value = d.hopper.height;
      action.getFieldByName('dimensions[hopper][angle]').value = d.hopper.angle;
      action.getFieldByName('dimensions[hopper][truncatedRadius]').value = d.hopper.truncatedRadius;
      this._set();
      // set the copied binId in the hidden field
      this.shadowRoot.getElementById('binDimensionsCopiedBinId').value = autofillEntity.getLinkByRel('https://api.binsentry.com/rel/bin');
      // get the source barn name
      flash.success(`Bin dimensions have been copied from ${sourceBarn.name} > ${sourceBin.name}.`);
    } catch (error) {
      flash.critical(html`
        There was a problem copying the bin dimensions.
        <div><code>${error.message}</code></div>
      `);
      return;
    }
  }
}

window.customElements.define('work-order-bin-dimensions', WorkOrderBinDimensions);
